<script setup lang="ts">
interface ScalePrice {
  minQuantity: number
  price: number | { amount: number, currency: string }
}

const props = defineProps<{
  isScalePriceSheetOpen: boolean
  scalePrices: ScalePrice[]
  scaleAddToCart: (quantity: number) => Promise<void>
  maxQuantity?: number
}>()

const emit = defineEmits(['update:isScalePriceSheetOpen'])
const helper = useHelper()

const priceAmount = (price: ScalePrice['price']): number =>
  typeof price === 'number' ? price : price.amount

const sortedScalePrices = computed(() =>
  [...props.scalePrices]
    .filter(scale => !props.maxQuantity || scale.minQuantity <= props.maxQuantity)
    .sort((a, b) => a.minQuantity - b.minQuantity),
)

const isOpen = ref(props.isScalePriceSheetOpen)

watch(() => props.isScalePriceSheetOpen, (newVal) => {
  isOpen.value = newVal
})

watch(isOpen, (newVal) => {
  emit('update:isScalePriceSheetOpen', newVal)
})
</script>

<template>
  <v-bottom-sheet
    v-model="isOpen"
  >
    <v-card>
      <v-col
        cols="12"
        md="8"
        lg="6"
        xl="3"
        class="mx-auto mt-4"
      >
        <div class="text-h5 mb-2">
          <v-icon class="text-vm-red-700">
            veticon-tag
          </v-icon>
          {{ $t('scaleDiscount.headline') }}
        </div>
        <div>
          {{ $t('scaleDiscount.text') }}
        </div>
      </v-col>

      <!-- Scale price list -->
      <v-row>
        <v-col
          cols="12"
          md="8"
          lg="6"
          xl="3"
          class="mx-auto mt-4"
        >
          <v-sheet>
            <v-col
              v-for="(scalePrice, index) in sortedScalePrices"
              :key="`scale-price-${index}`"
              cols="12"
              :class="{ 'bg-pale-grey': index % 2 === 0 }"
            >
              <div class="d-flex align-center">
                <span class="font-weight-bold">
                  {{ $t('scaleDiscount.piece', { quantity: scalePrice.minQuantity || 1 }) }}
                  <span class="text-vm-red-700">
                    {{ helper.currencyToString(priceAmount(scalePrice.price)) }}/
                  </span>
                  {{ $t('scaleDiscount.perPiece') }}
                </span>
                <v-btn
                  v-if="scalePrice.minQuantity > 0"
                  color="primary"
                  class="ml-auto"
                  width="240"
                  data-track-id="product_scale_add_to_cart"
                  @click="props.scaleAddToCart(scalePrice.minQuantity)"
                >
                  {{ $t('scaleDiscount.addToCart', { quantity: scalePrice.minQuantity }) }}
                </v-btn>
              </div>
            </v-col>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card>
  </v-bottom-sheet>
</template>
